<template>
    <div class="global-search-wrapper">
        <div class="search-trigger" @click="openSearch">
            <i class="fas fa-search"></i> {{ t.search }}...
        </div>

        <div v-if="isOpen" class="search-overlay" @click="closeSearch">
            <div class="search-container" @click.stop>
                <div class="search-header">
                    <div class="search-input-container">
                        <input
                            ref="searchInput"
                            v-model="searchQuery"
                            type="text"
                            :placeholder="t.placeholder"
                            @input="isLoading = true && handleSearch()"
                            @keydown.down.prevent="selectNextResult"
                            @keydown.up.prevent="selectPreviousResult"
                            @keydown.enter="handleEnterKey"
                            @keydown.escape="closeSearch"
                        >
                        <div class="search-icon" :class="{ 'loading': isLoading }">
                            <i class="fa-solid fa-magnifying-glass"></i>
                        </div>
                        <button @click="closeSearch" class="close-button">
                            <i class="fa-solid fa-times"></i>
                        </button>
                    </div>
                </div>

                <div class="search-results">
                    <div v-if="isLoading" class="loading-state">
                        <div class="spinner"></div>
                        <p>{{ t.loading }}</p>
                    </div>

                    <div v-else-if="searchResults.length > 0" class="results-container">
                        <div class="quick-stats d-flex justify-content-between" v-if="totalResults > 0">
                            <span class="result-count">
                                {{ searchResults.length !== totalResults ? searchResults.length + ' / ' : '' }} {{ totalResults }} {{ totalResults === 1 ? t.result : t.results }} {{ t.for }}
                                <span class="query-text">"{{ searchQuery }}"</span>
                            </span>

                            <a :href="searchPage" v-if="searchResults.length < totalResults">{{ t.view_all_results }} <i class="far fa-arrow-right"></i></a>
                        </div>

                        <ul class="result-list">
                            <li v-for="(result, index) in limitedResults"
                                :key="`${result.id}-${index}`"
                                class="result-item"
                                :class="{'selected': selectedIndex === index, [type(result)]: true}"
                                @mouseover="selectedIndex = index">
                                <SearchResults :result="result" :current-language="currentLanguage"/>
                            </li>
                        </ul>

                        <div v-if="totalResults > 10" class="view-all-container">
                            <button @click="goToSearchPage" class="btn btn-primary">
                                {{ t.view_all_results }} ({{ totalResults }})
                                <i class="fa-solid fa-arrow-right"></i>
                            </button>
                        </div>
                    </div>

                    <div v-else-if="searchQuery.length >= 2" class="no-results">
                        <div class="no-results-icon">
                            <i class="fa-solid fa-search"></i>
                        </div>
                        <h3>{{ t.no_results_title }}</h3>
                        <p>{{ t.no_results_text.replace(':query', searchQuery) }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, nextTick} from 'vue';
import {useDebounceFn} from '@vueuse/core';
import axios from 'axios';
import SearchResults from '../components/SearchResults.vue';

// Reactive data
const isOpen        = ref(false);
const searchQuery   = ref('');
const searchResults = ref([]);
const isLoading     = ref(false);
const totalResults  = ref(0);
const searchPage    = ref('/nl/zoeken')
const searchInput   = ref(null);
const selectedIndex = ref(-1);
const translations  = ref({});

// Get current language from the URL or default to 'nl'
const currentLanguage = computed(() => {
    const path     = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    return (segments.length > 0 && ['nl', 'en'].includes(segments[0])) ? segments[0] : 'nl';
});

// Translation helper with additional quick search translations
const t = computed(() => translations.value.search || {
    placeholder:         'Zoeken...',
    no_results_title:    'Geen resultaten gevonden',
    no_results_text:     'Geen resultaten gevonden voor ":query"',
    result:              'resultaat',
    results:             'resultaten',
    for:                 'voor',
    loading:             'Zoeken...',
    view_all_results:    'Alle resultaten bekijken',
    try_advanced_search: 'Probeer geavanceerd zoeken',
    categories:          {}
});

// Load translations
const loadTranslations = async() => {
    try {
        const {data}       = await axios.get('/api/search/translations', {
            params: {locale: currentLanguage.value}
        });
        translations.value = data;
    } catch(error) {
        console.error('Failed to load translations:', error);
    }
};

// Limited results for quick search (max 10)
const limitedResults = computed(() => {
    return searchResults.value.slice(0, 10);
});

// Handle search with debounce
const handleSearch = useDebounceFn(async() => {
    if(searchQuery.value.length < 2) {
        searchResults.value = [];
        totalResults.value  = 0;

        isLoading.value = false;
        return;
    }

    isLoading.value     = true;
    selectedIndex.value = -1;

    try {
        const {data} = await axios.get('/api/search', {
            params: {
                query:       searchQuery.value,
                lang:        currentLanguage.value,
                quickSearch: true,
                limit:       6
            }
        });

        searchResults.value = data.hits || [];
        totalResults.value  = data.estimatedTotalHits || data.hits.length;
        searchPage.value    = data.searchPage

    } catch(error) {
        console.error('Search error:', error);
        searchResults.value = [];
        totalResults.value  = 0;
    } finally {
        isLoading.value = false;
    }
}, 300);

// Open/close search
const openSearch = async() => {
    isOpen.value = true;
    await nextTick();
    searchInput.value?.focus();
};

const closeSearch = () => {
    isOpen.value        = false;
    searchQuery.value   = '';
    searchResults.value = [];
    totalResults.value  = 0;
    selectedIndex.value = -1;
};

// Go to main search page
const goToSearchPage = () => {
    const searchUrl      = `${searchPage.value}`;
    window.location.href = searchUrl;
};

// Keyboard navigation functions (only for visible results)
const selectNextResult = () => {
    if(limitedResults.value.length > 0) {
        if(selectedIndex.value < limitedResults.value.length - 1) {
            selectedIndex.value++;
        } else {
            selectedIndex.value = 0;
        }
    }
};

const selectPreviousResult = () => {
    if(limitedResults.value.length > 0) {
        if(selectedIndex.value > 0) {
            selectedIndex.value--;
        } else {
            selectedIndex.value = limitedResults.value.length - 1;
        }
    }
};

const handleEnterKey = () => {
    if(selectedIndex.value >= 0 && selectedIndex.value < limitedResults.value.length) {
        const selectedResult = limitedResults.value[selectedIndex.value];
        const targetUrl      = selectedResult.product_url || selectedResult.url;
        if(targetUrl) {
            window.location.href = targetUrl;
        }
    } else if(searchQuery.value.length >= 2) {
        // If no result selected but there's a query, go to search page
        goToSearchPage();
    }
};

// Helper to determine result type class
const type = (result) => {
    const singularKey = result.index_singular?.toLowerCase();

    switch(singularKey) {
        case 'medewerker':
        case 'employee':
            return 'employee-result'
        case 'nieuwsbericht':
        case 'headline':
        case 'news':
        case 'artikel':
            return 'headline-result'
        case 'oplossing':
        case 'solution':
            return 'solution-result'
        case 'product':
            return 'product-result'
        case 'productgroep':
        case 'product group':
            return 'product-group-result'
        case 'productvariatie':
        case 'product variation':
            return 'product-variation-result'
        case 'accessoire':
        case 'accessory':
            return 'product-accessory-result'
        case 'concept':
            return 'concept-result'
        case 'vacature':
        case 'vacancy':
            return 'vacancy-result'
        case 'stap':
        case 'step':
            return 'total-approach-step-result'
        case 'pagina':
        case 'page':
            return 'page-result'
        case 'klantverhaal':
            return 'testimonial-result'
        default:
            return 'default-result'
    }
};

// Load translations on mount
onMounted(() => {
    loadTranslations();
});
</script>